import { Fragment, useState } from "react";
import {
  useMotifBaremes,
  useCreateMotifBareme,
  useUpdateMotifBareme,
  useDeleteMotifBareme,
  useMotifEcheancesParMotif,
  useRemplacerEcheancierParMotif,
  useClassesEcole,
  useNiveauxEcole,
} from "@/lib/backoffice-hooks";
import type { ClasseLite, MotifBareme, MotifEcheance } from "@/lib/api";
import { useAnneeScope } from "@/lib/annee-scope";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Trash2, ChevronDown, ChevronRight, X } from "lucide-react";
import { friendlyError } from "@/lib/errors";

const fcfa = (n: number) => `${n.toLocaleString("fr-FR")} F`;

/**
 * Un « groupe » de la grille : un NIVEAU et ses classes — ou une classe
 * orpheline (niveau inconnu), traitée comme un groupe d'une seule classe.
 * La saisie d'un montant sur le groupe s'applique à TOUTES ses classes.
 */
interface GroupeTarif {
  key: string;
  libelle: string;
  classes: { classe: ClasseLite; bareme?: MotifBareme }[];
}

/** État tarifaire d'un groupe : uniforme (fan-out simple) ou divergent. */
function etatGroupe(g: GroupeTarif) {
  const tarifees = g.classes.filter((x) => x.bareme);
  const montants = Array.from(new Set(tarifees.map((x) => x.bareme!.montant)));
  const uniforme = montants.length === 1 && tarifees.length === g.classes.length;
  return {
    nbTarifees: tarifees.length,
    uniforme,
    montantUniforme: uniforme ? montants[0] : null,
    min: montants.length ? Math.min(...montants) : null,
    max: montants.length ? Math.max(...montants) : null,
  };
}

/**
 * Tarifs d'un motif — saisie PAR NIVEAU, stockage par classe.
 *
 * Les fiches SEDEC fixent les prix par niveau (CM1 = un montant) : la grille
 * liste donc les niveaux ; un montant saisi sur la ligne CM1 est recopié sur
 * CM1A et CM1B (fan-out). Déplier un niveau permet de tarifer une classe à
 * part et de gérer les tranches. Le motif, lui, ne parle jamais d'argent.
 */
export default function MotifBaremesPanel({
  motifId,
  motifLibelle,
  disabled,
}: {
  motifId: string;
  motifLibelle: string;
  disabled?: boolean;
}) {
  const { anneeId } = useAnneeScope();
  const { data: baremes, isLoading } = useMotifBaremes(motifId);
  const { data: classes, isLoading: classesLoading } = useClassesEcole();
  const { data: niveaux, isLoading: niveauxLoading } = useNiveauxEcole();
  const { data: echeances } = useMotifEcheancesParMotif(motifId);
  const createBareme = useCreateMotifBareme();
  const updateBareme = useUpdateMotifBareme();
  const deleteBareme = useDeleteMotifBareme(motifId);

  /** Groupe déplié (un seul à la fois). */
  const [ouvert, setOuvert] = useState<string | null>(null);
  /** Saisies en cours, par clé de groupe ou de classe — non encore enregistrées. */
  const [saisie, setSaisie] = useState<Record<string, string>>({});
  /** Modification en attente du pop-up de confirmation — SEULE porte vers l'écriture. */
  const [confirmation, setConfirmation] = useState<{ g: GroupeTarif; brut: string } | null>(null);

  const chargement = isLoading || classesLoading || niveauxLoading;
  const parClasse = new Map((baremes ?? []).map((b) => [b.classeId, b]));
  const lignesClasses = classes ?? [];
  const sansTarif = lignesClasses.filter((c) => !parClasse.has(c.id)).length;

  // Niveaux triés par ordre pédagogique ; les classes sans niveau reconnu
  // forment chacune leur propre groupe (repli sûr, jamais invisible).
  const groupes: GroupeTarif[] = [];
  const niveauxTries = [...(niveaux ?? [])].sort((a, b) => a.ordre - b.ordre);
  for (const n of niveauxTries) {
    const cls = lignesClasses
      .filter((c) => c.niveauId === n.id)
      .sort((a, b) => a.libelle.localeCompare(b.libelle, "fr"));
    if (cls.length > 0) {
      groupes.push({
        key: n.id,
        libelle: n.libelle,
        classes: cls.map((c) => ({ classe: c, bareme: parClasse.get(c.id) })),
      });
    }
  }
  const connus = new Set(niveauxTries.map((n) => n.id));
  for (const c of lignesClasses.filter((x) => !x.niveauId || !connus.has(x.niveauId))) {
    groupes.push({
      key: `classe:${c.id}`,
      libelle: c.libelle,
      classes: [{ classe: c, bareme: parClasse.get(c.id) }],
    });
  }

  /** Valeur en vigueur telle qu'affichée dans le champ (uniforme, sinon vide). */
  function valeurEnVigueur(g: GroupeTarif) {
    const etat = etatGroupe(g);
    return etat.montantUniforme != null ? String(etat.montantUniforme) : "";
  }

  function valeurAffichee(g: GroupeTarif) {
    if (saisie[g.key] !== undefined) return saisie[g.key];
    return valeurEnVigueur(g);
  }

  /** La saisie change-t-elle vraiment quelque chose ? Sinon, pas de bouton Valider. */
  function estModifie(g: GroupeTarif) {
    const brut = saisie[g.key];
    if (brut === undefined) return false;
    const montant = Number(brut);
    if (brut.trim() === "" || montant <= 0) {
      // Vider n'a de sens (retrait) que s'il existe un tarif à retirer.
      return etatGroupe(g).nbTarifees > 0;
    }
    return brut.trim() !== valeurEnVigueur(g);
  }

  function annulerSaisie(key: string) {
    setSaisie((s) => {
      const { [key]: _retire, ...reste } = s;
      return reste;
    });
  }

  function ouvrirConfirmation(g: GroupeTarif) {
    setConfirmation({ g, brut: saisie[g.key] as string });
  }

  /** Boutons « Valider » + croix d'abandon, visibles seulement quand la ligne a changé. */
  function boutonsValidation(g: GroupeTarif, suffix: string) {
    if (!estModifie(g)) return null;
    return (
      <span className="inline-flex items-center gap-1 ml-2">
        <Button
          size="sm"
          className="h-7 px-2 bg-navy text-white text-xs"
          onClick={() => ouvrirConfirmation(g)}
          data-testid={`button-valider-${suffix}`}
        >
          Valider
        </Button>
        <button
          className="text-slate-400 hover:text-slate-600"
          title="Annuler la saisie"
          onClick={() => annulerSaisie(g.key)}
          data-testid={`button-annuler-${suffix}`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </span>
    );
  }

  /** Aucun tarif / fourchette quand les classes divergent — jamais un faux prix unique. */
  function placeholderGroupe(g: GroupeTarif) {
    const etat = etatGroupe(g);
    if (etat.nbTarifees === 0) return "Aucun tarif";
    return etat.min === etat.max
      ? `${fcfa(etat.min as number)} (${etat.nbTarifees}/${g.classes.length} classes)`
      : `${fcfa(etat.min as number)} à ${fcfa(etat.max as number)}`;
  }

  /**
   * Applique la modification CONFIRMÉE par le pop-up, pour toutes les classes
   * du groupe : création, mise à jour, retrait si vidé. La saisie n'est
   * effacée qu'après succès — en cas d'erreur, la valeur tapée reste à
   * l'écran pour réessayer.
   */
  async function enregistrer(g: GroupeTarif, brut: string) {
    const montant = Number(brut);
    const vide = brut.trim() === "" || montant <= 0;
    const pluriel = g.classes.length > 1 ? ` (${g.classes.length} classes)` : "";
    try {
      if (vide) {
        const existants = g.classes.filter((x) => x.bareme);
        await Promise.all(existants.map((x) => deleteBareme.mutateAsync(x.bareme!.id)));
        annulerSaisie(g.key);
        toast.success(`Tarif retiré pour ${g.libelle}${pluriel}`);
        return;
      }
      const aChanger = g.classes.filter((x) => !x.bareme || x.bareme.montant !== montant);
      await Promise.all(
        aChanger.map((x) => {
          const commun = {
            motifId,
            classeId: x.classe.id,
            anneeScolaireId: anneeId as string,
            montant,
          };
          return x.bareme
            ? updateBareme.mutateAsync({ id: x.bareme.id, data: commun })
            : createBareme.mutateAsync(commun);
        }),
      );
      annulerSaisie(g.key);
      toast.success(`${g.libelle} : ${fcfa(montant)}${pluriel}`);
    } catch (e: unknown) {
      toast.error(friendlyError(e));
    }
  }

  /** Groupe éphémère pour la dérogation d'UNE classe dans un niveau déplié. */
  const grouperClasse = (x: { classe: ClasseLite; bareme?: MotifBareme }): GroupeTarif => ({
    key: `classe:${x.classe.id}`,
    libelle: x.classe.libelle,
    classes: [x],
  });

  function resumeTranches(g: GroupeTarif) {
    const etat = etatGroupe(g);
    if (etat.nbTarifees === 0) return "·";
    const ids = new Set(g.classes.filter((x) => x.bareme).map((x) => x.bareme!.id));
    const nb = grouperTranches((echeances ?? []).filter((e) => e.motifBaremeId && ids.has(e.motifBaremeId))).length;
    if (!etat.uniforme) return "par classe";
    return nb === 0 ? "Comptant" : `${nb} tranche${nb > 1 ? "s" : ""}`;
  }

  return (
    <div className="bg-slate-50/70 px-4 py-3 border-t border-slate-100" data-testid={`baremes-${motifId}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Tarifs par niveau · {motifLibelle}
        </p>
        <p className="text-xs text-slate-500">
          Un montant saisi sur un niveau s'applique à toutes ses classes.
          Rien ne s'enregistre sans « Valider » puis confirmation.
        </p>
      </div>

      {chargement && <Loader2 className="w-4 h-4 animate-spin text-navy" />}

      {!chargement && lignesClasses.length === 0 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
          Aucune classe dans cette école : créez-les d'abord dans la page « Classes » du menu.
        </p>
      )}

      {!chargement && lignesClasses.length > 0 && (
        <>
          {sansTarif > 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-2">
              {sansTarif} classe(s) sans tarif : leurs élèves ne peuvent pas être facturés
              pour « {motifLibelle} ».
            </p>
          )}
          <table className="w-full text-sm">
            <tbody>
              {groupes.map((g) => {
                const estOuvert = ouvert === g.key;
                const etat = etatGroupe(g);
                return (
                  <Fragment key={g.key}>
                    <tr className="border-t border-slate-100" data-testid={`ligne-niveau-${g.libelle}`}>
                      <td className="py-1.5 w-52">
                        <button
                          className="inline-flex items-center gap-1 font-medium hover:underline"
                          onClick={() => setOuvert(estOuvert ? null : g.key)}
                          data-testid={`button-ouvrir-${g.libelle}`}
                        >
                          {estOuvert ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                          {g.libelle}
                        </button>
                        {g.classes.length > 1 && (
                          <span className="block pl-4 text-[11px] text-slate-400">
                            {g.classes.map((x) => x.classe.libelle).join(", ")}
                          </span>
                        )}
                      </td>
                      <td className="w-64">
                        <span className="inline-flex items-center">
                          <Input
                            type="number"
                            min={0}
                            placeholder={placeholderGroupe(g)}
                            className="h-8 w-36 text-right"
                            disabled={disabled}
                            value={valeurAffichee(g)}
                            onChange={(e) => setSaisie((s) => ({ ...s, [g.key]: e.target.value }))}
                            data-testid={`input-tarif-${g.libelle}`}
                          />
                          {boutonsValidation(g, g.libelle)}
                        </span>
                      </td>
                      <td className="pl-3 text-xs text-slate-500" data-testid={`tranches-resume-${g.libelle}`}>
                        {resumeTranches(g)}
                      </td>
                    </tr>
                    {estOuvert && (
                      <tr data-testid={`detail-niveau-${g.libelle}`}>
                        <td colSpan={3} className="p-0">
                          <div className="bg-white border-l-4 border-navy/20 px-4 py-3 space-y-3">
                            {g.classes.length > 1 && (
                              <div>
                                <p className="text-xs font-semibold text-slate-600 mb-1">
                                  Classes de {g.libelle} : corrigez ici une classe qui déroge au niveau.
                                </p>
                                <table className="w-full text-sm">
                                  <tbody>
                                    {g.classes.map((x) => {
                                      const gc = grouperClasse(x);
                                      return (
                                        <tr key={x.classe.id} data-testid={`ligne-classe-${x.classe.libelle}`}>
                                          <td className="py-1 w-48 pl-4">{x.classe.libelle}</td>
                                          <td className="w-64">
                                            <span className="inline-flex items-center">
                                              <Input
                                                type="number"
                                                min={0}
                                                placeholder="Aucun tarif"
                                                className="h-8 w-36 text-right"
                                                disabled={disabled}
                                                value={valeurAffichee(gc)}
                                                onChange={(e) =>
                                                  setSaisie((s) => ({ ...s, [gc.key]: e.target.value }))
                                                }
                                                data-testid={`input-tarif-classe-${x.classe.libelle}`}
                                              />
                                              {boutonsValidation(gc, `classe-${x.classe.libelle}`)}
                                            </span>
                                          </td>
                                          <td />
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {etat.uniforme && (
                              <Echeancier
                                motifId={motifId}
                                titre={g.libelle}
                                scope={g.classes.map((x) => x.bareme!.id)}
                                montantRef={etat.montantUniforme as number}
                                toutes={echeances ?? []}
                                disabled={disabled}
                              />
                            )}
                            {!etat.uniforme && etat.nbTarifees > 0 && (
                              <div className="space-y-2">
                                <p className="text-xs text-amber-700" data-testid={`tranches-divergent-${g.libelle}`}>
                                  Les tarifs de ce niveau diffèrent selon la classe : les tranches se gèrent classe par classe.
                                </p>
                                {g.classes
                                  .filter((x) => x.bareme)
                                  .map((x) => (
                                    <Echeancier
                                      key={x.classe.id}
                                      motifId={motifId}
                                      titre={x.classe.libelle}
                                      scope={[x.bareme!.id]}
                                      montantRef={x.bareme!.montant}
                                      toutes={echeances ?? []}
                                      disabled={disabled}
                                    />
                                  ))}
                              </div>
                            )}
                            {etat.nbTarifees === 0 && (
                              <p className="text-xs text-slate-500" data-testid={`tranches-sans-tarif-${g.libelle}`}>
                                Fixez d'abord un tarif pour définir des tranches.
                              </p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {confirmation && (() => {
        const { g, brut } = confirmation;
        const etat = etatGroupe(g);
        const montant = Number(brut);
        const retrait = brut.trim() === "" || montant <= 0;
        const avant =
          etat.nbTarifees === 0
            ? "aucun tarif"
            : etat.montantUniforme != null
              ? fcfa(etat.montantUniforme)
              : `${fcfa(etat.min as number)} à ${fcfa(etat.max as number)}`;
        const noms = g.classes.map((x) => x.classe.libelle).join(", ");
        return (
          <Dialog
            open
            onOpenChange={(o) => {
              /* v8 ignore start -- défensif : sans DialogTrigger, Radix n'appelle onOpenChange qu'avec o=false (fermeture) ; la branche o=true est inatteignable */
              if (!o) setConfirmation(null);
              /* v8 ignore stop */
            }}
          >
            <DialogContent className="max-w-md" data-testid="dialog-confirm-tarif">
              <DialogHeader>
                <DialogTitle>{retrait ? "Retirer le tarif" : "Confirmer le tarif"}</DialogTitle>
                <DialogDescription>Rien n'est enregistré sans cette confirmation.</DialogDescription>
              </DialogHeader>
              <p className="text-sm" data-testid="recap-confirm-tarif">
                {retrait ? (
                  <>
                    Retirer le tarif de <b>{g.libelle}</b> (actuellement {avant}) :
                    les élèves de {noms} ne pourront plus être facturés pour « {motifLibelle} ».
                  </>
                ) : (
                  <>
                    <b>{g.libelle}</b> : {avant} → <b>{fcfa(montant)}</b>, appliqué à{" "}
                    {g.classes.length} classe(s) : {noms}.
                  </>
                )}
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConfirmation(null)} data-testid="button-cancel-tarif">
                  Annuler
                </Button>
                <Button
                  className={retrait ? "bg-red-600 hover:bg-red-700 text-white" : "bg-navy hover:bg-navy/90 text-white"}
                  onClick={() => {
                    setConfirmation(null);
                    enregistrer(g, brut);
                  }}
                  data-testid="button-confirm-tarif"
                >
                  {retrait ? "Retirer" : "Confirmer"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        );
      })()}
    </div>
  );
}

/** Tranche vue côté école : la même échéance recopiée sur N barèmes de classes. */
interface TrancheGroupee {
  libelle: string;
  montant: number;
  dateEcheance: string;
  ordre: number;
  ids: string[];
}

function grouperTranches(liste: MotifEcheance[]): TrancheGroupee[] {
  const parCle = new Map<string, TrancheGroupee>();
  for (const e of liste) {
    const cle = `${e.libelle}|${e.montant}|${e.dateEcheance}`;
    const existante = parCle.get(cle);
    if (existante) {
      existante.ids.push(e.id);
    } else {
      parCle.set(cle, {
        libelle: e.libelle,
        montant: e.montant,
        dateEcheance: e.dateEcheance,
        ordre: e.ordre,
        ids: [e.id],
      });
    }
  }
  return Array.from(parCle.values()).sort(
    (a, b) => a.ordre - b.ordre || a.dateEcheance.localeCompare(b.dateEcheance),
  );
}

/** Ligne d'un échéancier en cours de saisie (montant tel que tapé). */
interface LigneTranche {
  libelle: string;
  montant: string;
  dateEcheance: string;
}

const libelleTranche = (n: number) => (n === 1 ? "1re tranche" : `${n}e tranche`);

/**
 * Ce qui empêche d'enregistrer un échéancier, dit en clair ; {@code null} s'il
 * peut partir. Mêmes règles que le serveur : chaque tranche complète, dates
 * strictement croissantes, somme égale au tarif. Une liste vide est permise
 * (la famille règle tout en une fois).
 */
export function erreurEcheancier(lignes: LigneTranche[], tarif: number): string | null {
  for (let i = 0; i < lignes.length; i++) {
    const l = lignes[i];
    if (!l.libelle.trim() || !(Number(l.montant) > 0) || !l.dateEcheance) {
      return `Tranche ${i + 1} : libellé, montant et date limite sont obligatoires.`;
    }
    if (i > 0 && l.dateEcheance <= lignes[i - 1].dateEcheance) {
      return `Tranche ${i + 1} : sa date limite doit suivre celle de la tranche ${i}.`;
    }
  }
  const total = lignes.reduce((s, l) => s + Number(l.montant), 0);
  if (lignes.length === 0 || total === tarif) return null;
  return total < tarif
    ? `Reste à répartir : ${fcfa(tarif - total)} sur ${fcfa(tarif)}.`
    : `Les tranches dépassent le tarif de ${fcfa(total - tarif)}.`;
}

/**
 * Échéancier d'un niveau (ou d'une classe qui déroge).
 *
 * Décision du 28/07 : un parent règle soit la totalité, soit une tranche
 * exacte. Chaque tranche est recopiée sur le barème de chaque classe du
 * périmètre — le paiement en ligne exige la tranche sur le barème de LA
 * classe de l'élève — puis regroupée à l'affichage.
 *
 * Audit SCOM-03 du 21/10/2026 : l'ajout et le retrait tranche par tranche
 * tombaient en 500 (la base exige que la somme des tranches égale le tarif, ce
 * qu'une tranche seule ne vérifie jamais). On saisit désormais TOUTES les
 * tranches d'un coup, contrôle « somme = tarif » à l'écran, puis un seul PUT
 * atomique par barème de classe (même geste que CATHO SCHOOL PRO).
 */
function Echeancier({
  motifId,
  titre,
  scope,
  montantRef,
  toutes,
  disabled,
}: {
  motifId: string;
  titre: string;
  scope: string[];
  montantRef: number;
  toutes: MotifEcheance[];
  disabled?: boolean;
}) {
  const remplacer = useRemplacerEcheancierParMotif(motifId);
  /** Échéancier en cours de saisie ; {@code null} = lecture. */
  const [brouillon, setBrouillon] = useState<LigneTranche[] | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const ids = new Set(scope);
  const tranches = grouperTranches(toutes.filter((e) => e.motifBaremeId && ids.has(e.motifBaremeId)));
  const total = tranches.reduce((s, t) => s + t.montant, 0);
  const reste = montantRef - total;

  function ouvrir() {
    setBrouillon(
      tranches.length > 0
        ? tranches.map((t) => ({ libelle: t.libelle, montant: String(t.montant), dateEcheance: t.dateEcheance }))
        : [{ libelle: libelleTranche(1), montant: "", dateEcheance: "" }],
    );
  }

  function changer(i: number, champ: keyof LigneTranche, valeur: string) {
    setBrouillon((b) => (b as LigneTranche[]).map((l, j) => (j === i ? { ...l, [champ]: valeur } : l)));
  }

  async function enregistrer(lignes: LigneTranche[]) {
    setEnvoi(true);
    const corps = lignes.map((l) => ({
      libelle: l.libelle.trim(),
      montant: Number(l.montant),
      dateEcheance: l.dateEcheance,
    }));
    try {
      await Promise.all(scope.map((baremeId) => remplacer.mutateAsync({ baremeId, tranches: corps })));
      toast.success(
        lignes.length === 0
          ? `Tranches retirées : la famille règle ${fcfa(montantRef)} en une fois`
          : `Échéancier enregistré : ${lignes.length} tranche(s)`,
      );
      setBrouillon(null);
    } catch (e: unknown) {
      toast.error(friendlyError(e));
    } finally {
      setEnvoi(false);
    }
  }

  if (brouillon) {
    const erreur = erreurEcheancier(brouillon, montantRef);
    return (
      <div data-testid={`echeancier-${titre}`}>
        <p className="text-xs font-semibold text-slate-600 mb-1.5">
          Tranches de {titre} · tarif {fcfa(montantRef)} · saisie de toutes les tranches
        </p>
        <div className="space-y-2 mb-2">
          {brouillon.map((l, i) => (
            <div key={i} className="flex flex-wrap items-end gap-2" data-testid={`ligne-tranche-${i}`}>
              <div className="w-40">
                <Label className="text-xs">Libellé</Label>
                <Input
                  className="h-8"
                  value={l.libelle}
                  onChange={(e) => changer(i, "libelle", e.target.value)}
                  data-testid={`input-tranche-libelle-${i}`}
                />
              </div>
              <div className="w-28">
                <Label className="text-xs">Montant</Label>
                <Input
                  className="h-8"
                  type="number"
                  min={0}
                  value={l.montant}
                  onChange={(e) => changer(i, "montant", e.target.value)}
                  data-testid={`input-tranche-montant-${i}`}
                />
              </div>
              <div className="w-36">
                <Label className="text-xs">Date limite</Label>
                <Input
                  className="h-8"
                  type="date"
                  value={l.dateEcheance}
                  onChange={(e) => changer(i, "dateEcheance", e.target.value)}
                  data-testid={`input-tranche-date-${i}`}
                />
              </div>
              <button
                className="h-8 text-slate-400 hover:text-red-600"
                title="Retirer cette tranche"
                onClick={() => setBrouillon(brouillon.filter((_, j) => j !== i))}
                data-testid={`button-retirer-ligne-${i}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
        <button
          className="text-xs text-navy hover:underline mb-2"
          onClick={() => setBrouillon([...brouillon, { libelle: libelleTranche(brouillon.length + 1), montant: "", dateEcheance: "" }])}
          data-testid="button-ajouter-ligne"
        >
          + Ajouter une tranche
        </button>
        <p
          className={`text-xs mb-2 ${erreur ? "text-amber-700" : "text-emerald-700"}`}
          data-testid={`controle-${titre}`}
        >
          {erreur ??
            (brouillon.length === 0
              ? `Sans tranche : la famille règle ${fcfa(montantRef)} en une fois.`
              : `Les tranches couvrent exactement le tarif (${fcfa(montantRef)}).`)}
        </p>
        <div className="flex gap-2">
          <Button
            size="sm"
            className="h-8 bg-navy text-white"
            disabled={erreur !== null || envoi}
            aria-busy={envoi}
            onClick={() => enregistrer(brouillon)}
            data-testid="button-enregistrer-echeancier"
          >
            Enregistrer l'échéancier
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            onClick={() => setBrouillon(null)}
            data-testid="button-annuler-echeancier"
          >
            Annuler
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`echeancier-${titre}`}>
      <p className="text-xs font-semibold text-slate-600 mb-1.5">
        Tranches de {titre} · tarif {fcfa(montantRef)}
      </p>

      {tranches.length === 0 ? (
        <p className="text-xs text-slate-500 mb-2">
          Aucune tranche : la famille règle {fcfa(montantRef)} en une fois.
        </p>
      ) : (
        <table className="w-full text-sm mb-2">
          <tbody>
            {tranches.map((t) => (
              <tr key={t.ids[0]} data-testid={`tranche-${t.ids[0]}`}>
                <td className="py-1">
                  {t.libelle}
                  {t.ids.length < scope.length && (
                    <span className="ml-2 text-[11px] text-amber-700" data-testid={`tranche-partielle-${t.ids[0]}`}>
                      {t.ids.length}/{scope.length} classes
                    </span>
                  )}
                </td>
                <td className="text-right font-mono w-28" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                  {fcfa(t.montant)}
                </td>
                <td className="text-right text-xs text-slate-500 w-28">{t.dateEcheance}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {tranches.length > 0 && (
        <p
          className={`text-xs mb-2 ${reste === 0 ? "text-emerald-700" : "text-amber-700"}`}
          data-testid={`reste-${titre}`}
        >
          {reste === 0
            ? `Les tranches couvrent exactement le tarif (${fcfa(total)}).`
            : reste > 0
              ? `Reste à répartir : ${fcfa(reste)} sur ${fcfa(montantRef)}.`
              : `Les tranches dépassent le tarif de ${fcfa(-reste)}.`}
        </p>
      )}

      {!disabled && (
        <Button
          size="sm"
          variant="outline"
          className="h-8"
          onClick={ouvrir}
          data-testid="button-modifier-echeancier"
        >
          {tranches.length > 0 ? "Modifier les tranches" : "Découper en tranches"}
        </Button>
      )}
    </div>
  );
}
